import { useState, useEffect } from 'react';
import { Users, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useFamilyBonding } from '@/hooks/useFamilyBonding';
import { FamilyActivityItem } from '@/components/family-bonding/FamilyActivityItem';
import { AddCustomActivityDialog } from '@/components/family-bonding/AddCustomActivityDialog';
import { WeeklyReviewDialog } from '@/components/family-bonding/WeeklyReviewDialog';

const SUGGESTION_CHIPS = [
  'Cook together',
  'Game night',
  'Walk outdoors',
  'Movie evening',
  'Read together',
  'Arts & crafts',
];

export function FamilyBondingContent() {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  const {
    activities,
    completedActivities,
    completedCount,
    totalCount,
    progressPercent,
    isLoading,
    needsWeeklyReset,
    hasSeenFeature,
    toggleActivity,
    addCustomActivity,
    dismissActivity,
    removeCustomActivity,
    resetWeek,
    markFeatureSeen,
    getWeeklySummary,
  } = useFamilyBonding();

  // Show weekly review dialog when new week starts
  useEffect(() => {
    if (needsWeeklyReset && hasSeenFeature) {
      setShowReviewDialog(true);
    }
  }, [needsWeeklyReset, hasSeenFeature]);

  // Mark feature as seen on mount
  useEffect(() => {
    if (!hasSeenFeature) {
      markFeatureSeen();
    }
  }, [hasSeenFeature, markFeatureSeen]);

  const handleWeeklyReviewClose = () => {
    setShowReviewDialog(false);
    resetWeek();
  };

  const handleChipAdd = (text: string) => {
    addCustomActivity({
      title: text,
      icon: 'heart',
      category: 'custom',
    });
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-rose-200 dark:bg-rose-800 rounded w-1/4" />
          <div className="h-2 bg-rose-200 dark:bg-rose-800 rounded" />
          <div className="h-12 bg-rose-200 dark:bg-rose-800 rounded" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {/* Progress Bar */}
        {totalCount > 0 && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-rose-700 dark:text-rose-300 font-medium">
                {completedCount}/{totalCount} activities
              </span>
              <span className="text-rose-600 dark:text-rose-400">
                {progressPercent}%
              </span>
            </div>
            <Progress 
              value={progressPercent} 
              className="h-2 bg-rose-200 dark:bg-rose-900 [&>div]:bg-rose-500" 
            />
          </div>
        )}

        {/* Activity List */}
        {activities.length > 0 ? (
          <div className="max-h-48 overflow-y-auto space-y-2">
            {activities.map((activity) => (
              <FamilyActivityItem
                key={activity.id}
                activity={activity}
                isCompleted={completedActivities.includes(activity.id)}
                onToggle={() => toggleActivity(activity.id)}
                onDismiss={() => dismissActivity(activity.id)}
                onRemove={activity.isCustom ? () => removeCustomActivity(activity.id) : undefined}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-5 bg-rose-100/50 dark:bg-rose-900/30 rounded-lg border border-rose-200 dark:border-rose-800">
            <Users className="h-7 w-7 mx-auto mb-2 text-rose-400" />
            <p className="text-sm font-medium text-rose-700 dark:text-rose-300 mb-1">
              What do you enjoy doing together?
            </p>
            <p className="text-xs text-rose-600 dark:text-rose-400 mb-3">
              Tap a suggestion or add your own
            </p>
            {/* Suggestion chips */}
            <div className="flex flex-wrap gap-1.5 justify-center px-3">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  onClick={() => handleChipAdd(chip)}
                  className="text-xs px-3 py-1.5 rounded-full bg-rose-200/70 dark:bg-rose-800/50 text-rose-700 dark:text-rose-300 hover:bg-rose-300 dark:hover:bg-rose-700 transition-colors"
                >
                  + {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add Custom Activity Button */}
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2 border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/50"
          onClick={() => setShowAddDialog(true)}
        >
          <Plus className="h-4 w-4" />
          Add Custom Activity
        </Button>

        {/* Footer */}
        <p className="text-xs text-center text-rose-600/70 dark:text-rose-400/70 italic flex items-center justify-center gap-1">
          <RefreshCw className="h-3 w-3" />
          Resets every Monday — build lasting traditions
        </p>
      </div>

      <AddCustomActivityDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onAdd={addCustomActivity}
      />

      <WeeklyReviewDialog
        open={showReviewDialog}
        onClose={handleWeeklyReviewClose}
        summary={getWeeklySummary()}
      />
    </>
  );
}
